import React, { useRef, useEffect, useCallback, useMemo } from 'react';
import * as d3 from 'd3';

const NODE_COLORS = {
  SERVICE: '#3b82f6',
  TEAM: '#22c55e',
  PATTERN: '#a855f7',
  API: '#eab308',
  REGULATION: '#ef4444',
  DOCUMENT: '#06b6d4',
  CAPABILITY: '#f97316',
  PRODUCT: '#ec4899',
  DEFAULT: '#6b7280',
};

function MindMapGraph({ entities, relationships, selectedEntity, onSelectEntity, width, height }) {
  const svgRef = useRef(null);

  const graphData = useMemo(() => {
    // Build hierarchy - use Atlas Bank as center node, fallback to most connected
    const connectionCount = {};
    relationships.forEach(rel => {
      connectionCount[rel.source_id] = (connectionCount[rel.source_id] || 0) + 1;
      connectionCount[rel.target_id] = (connectionCount[rel.target_id] || 0) + 1;
    });

    // Find root - prefer "Atlas Financial" as center (most connected enterprise entity)
    let root = entities.find(e => e.id === 'ent:atlas-financial');

    // Fallback to most connected SERVICE or CAPABILITY if Atlas Bank not found
    if (!root) {
      let maxConnections = 0;
      root = entities.find(e => e.entity_type === 'SERVICE') || entities[0];
      entities.forEach(e => {
        const count = connectionCount[e.id] || 0;
        if (count > maxConnections && (e.entity_type === 'SERVICE' || e.entity_type === 'CAPABILITY')) {
          maxConnections = count;
          root = e;
        }
      });
    }

    if (!root) return { nodes: [], links: [] };

    // Build adjacency map
    const adjacency = {};
    entities.forEach(e => { adjacency[e.id] = new Set(); });
    relationships.forEach(rel => {
      if (adjacency[rel.source_id]) adjacency[rel.source_id].add(rel.target_id);
      if (adjacency[rel.target_id]) adjacency[rel.target_id].add(rel.source_id);
    });

    // BFS to assign levels from root
    const levels = { [root.id]: 0 };
    const queue = [root.id];
    const visited = new Set([root.id]);

    while (queue.length > 0) {
      const current = queue.shift();
      const neighbors = adjacency[current] || new Set();
      neighbors.forEach(neighbor => {
        if (!visited.has(neighbor)) {
          visited.add(neighbor);
          levels[neighbor] = levels[current] + 1;
          queue.push(neighbor);
        }
      });
    }

    // Assign unvisited nodes to outer ring
    entities.forEach(e => {
      if (levels[e.id] === undefined) {
        levels[e.id] = 4;
      }
    });

    // Create nodes with radial positions
    const nodesByLevel = {};
    entities.forEach(e => {
      const level = levels[e.id];
      if (!nodesByLevel[level]) nodesByLevel[level] = [];
      nodesByLevel[level].push(e);
    });

    const nodes = entities.map(entity => {
      const level = levels[entity.id];
      const nodesAtLevel = nodesByLevel[level];
      const indexAtLevel = nodesAtLevel.indexOf(entity);
      const angleStep = (2 * Math.PI) / nodesAtLevel.length;
      const angle = angleStep * indexAtLevel - Math.PI / 2;
      const radius = level * 120 + (level === 0 ? 0 : 80);

      return {
        id: entity.id,
        name: entity.name,
        type: entity.entity_type,
        description: entity.description,
        level,
        x: width / 2 + radius * Math.cos(angle),
        y: height / 2 + radius * Math.sin(angle),
        fx: width / 2 + radius * Math.cos(angle),
        fy: height / 2 + radius * Math.sin(angle),
        ...entity,
      };
    });

    const nodeIds = new Set(nodes.map(n => n.id));
    const links = relationships
      .filter(rel => nodeIds.has(rel.source_id) && nodeIds.has(rel.target_id))
      .map(rel => ({
        source: rel.source_id,
        target: rel.target_id,
        type: rel.rel_type,
      }));

    return { nodes, links };
  }, [entities, relationships, width, height]);

  useEffect(() => {
    if (!svgRef.current || !graphData.nodes.length) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const g = svg.append('g');

    // Zoom behavior
    const zoom = d3.zoom()
      .scaleExtent([0.3, 3])
      .on('zoom', (event) => g.attr('transform', event.transform));
    svg.call(zoom);

    // Draw concentric circles for levels
    const maxLevel = Math.max(...graphData.nodes.map(n => n.level));
    for (let i = 1; i <= maxLevel; i++) {
      g.append('circle')
        .attr('cx', width / 2)
        .attr('cy', height / 2)
        .attr('r', i * 120 + 80)
        .attr('fill', 'none')
        .attr('stroke', '#1f2937')
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '4,4');
    }

    // Draw curved links
    const linkGroup = g.append('g').attr('class', 'links');

    graphData.links.forEach(link => {
      const source = graphData.nodes.find(n => n.id === link.source);
      const target = graphData.nodes.find(n => n.id === link.target);
      if (!source || !target) return;

      // Calculate control point for curve
      const midX = (source.x + target.x) / 2;
      const midY = (source.y + target.y) / 2;
      const dx = target.x - source.x;
      const dy = target.y - source.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      // Curve toward center
      const centerX = width / 2;
      const centerY = height / 2;
      const curveStrength = Math.min(dist * 0.2, 50);
      const toCenter = {
        x: (centerX - midX) / dist * curveStrength,
        y: (centerY - midY) / dist * curveStrength,
      };

      const path = d3.path();
      path.moveTo(source.x, source.y);
      path.quadraticCurveTo(midX + toCenter.x, midY + toCenter.y, target.x, target.y);

      linkGroup.append('path')
        .attr('d', path.toString())
        .attr('fill', 'none')
        .attr('stroke', '#374151')
        .attr('stroke-width', 1.5)
        .attr('stroke-opacity', 0.6);
    });

    // Draw nodes
    const nodeGroup = g.append('g').attr('class', 'nodes');

    const node = nodeGroup.selectAll('g')
      .data(graphData.nodes)
      .enter()
      .append('g')
      .attr('transform', d => `translate(${d.x}, ${d.y})`)
      .attr('cursor', 'pointer')
      .on('click', (event, d) => {
        event.stopPropagation();
        onSelectEntity(d.id);
      });

    // Node circles
    node.append('circle')
      .attr('r', d => d.level === 0 ? 24 : d.type === 'SERVICE' ? 18 : 14)
      .attr('fill', d => NODE_COLORS[d.type] || NODE_COLORS.DEFAULT)
      .attr('stroke', d => d.id === selectedEntity ? '#a855f7' : '#0a0a0a')
      .attr('stroke-width', d => d.id === selectedEntity ? 3 : 2);

    // Type letter inside node
    node.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', '0.35em')
      .attr('fill', 'white')
      .attr('font-size', d => d.level === 0 ? '14px' : '11px')
      .attr('font-weight', 'bold')
      .attr('font-family', 'Arial, sans-serif')
      .text(d => d.type?.charAt(0) || '?');

    // Labels outside nodes
    node.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', d => (d.level === 0 ? 36 : d.type === 'SERVICE' ? 30 : 26))
      .attr('fill', '#d1d5db')
      .attr('font-size', '10px')
      .attr('font-family', 'Arial, sans-serif')
      .text(d => d.name?.length > 16 ? d.name.substring(0, 16) + '..' : d.name);

    // Click background to deselect
    svg.on('click', () => onSelectEntity(null));

    // Center and zoom to fit
    const initialTransform = d3.zoomIdentity
      .translate(0, 0)
      .scale(0.8);
    svg.call(zoom.transform, initialTransform);

  }, [graphData, width, height, selectedEntity, onSelectEntity]);

  return (
    <svg
      ref={svgRef}
      width={width}
      height={height}
      style={{ background: '#0a0a0a' }}
    />
  );
}

export default MindMapGraph;
